import { Spacing } from '@persona-id/design-tokens';
import { create } from 'jsondiffpatch';
import { isEmpty, omit } from 'lodash/fp';
import { useEffect } from 'react';
import styled from 'styled-components';

// TODO(IFD-686): Move these from the InquiryTemplateEditor folder to a shared location
import { getVtmplvDisplayName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import { DiffViewCard } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateJsonVersionDiff/InquiryTemplateJsonVersionDiff';
import { VerificationTemplateVersionDiffTable } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffVerification/InquiryTemplateVersionDiffVerification';
import { VerificationTemplateVersionDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';

const RootContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  position: relative;
  width: 100%;
  padding: ${Spacing[7]} ${Spacing[5]} 0 ${Spacing[5]};

  display: flex;
  flex-direction: column;
  overflow: scroll;
`;

interface VerificationTemplateDiffProps {
  verificationTemplateVersion: VerificationTemplateVersion;
  latestPublishedVerificationTemplateVersion: VerificationTemplateVersion | undefined;
  verificationTemplateVersionDiff: VerificationTemplateVersionDiff | undefined;
  showJsonDiff: boolean;
  handleLoadVersionDiff: () => void;
  handleLoadLatestPublishedVersion: () => void;
}

const VerificationTemplateDiff = ({
  verificationTemplateVersion,
  latestPublishedVerificationTemplateVersion,
  verificationTemplateVersionDiff,
  showJsonDiff,
  handleLoadVersionDiff,
  handleLoadLatestPublishedVersion,
}: VerificationTemplateDiffProps) => {
  useEffect(() => handleLoadVersionDiff(), [handleLoadVersionDiff]);
  useEffect(() => handleLoadLatestPublishedVersion(), [handleLoadLatestPublishedVersion]);

  if (verificationTemplateVersionDiff)
    return (
      <RootContainer>
        <ContentContainer>
          {showJsonDiff ? (
            latestPublishedVerificationTemplateVersion && (
              <VerificationTemplateJsonDiff
                currentVerificationTemplateVersion={verificationTemplateVersion}
                previousVerificationTemplateVersion={latestPublishedVerificationTemplateVersion}
              />
            )
          ) : (
            <VerificationTemplateVersionDiffTable
              verificationTemplateVersion={verificationTemplateVersion}
              verificationTemplateVersionChanges={verificationTemplateVersionDiff}
            />
          )}
        </ContentContainer>
      </RootContainer>
    );
  else return null;
};

interface VerificationTemplateJsonDiffProps {
  currentVerificationTemplateVersion: VerificationTemplateVersion;
  previousVerificationTemplateVersion: VerificationTemplateVersion;
}

const VerificationTemplateJsonDiff = ({
  currentVerificationTemplateVersion,
  previousVerificationTemplateVersion,
}: VerificationTemplateJsonDiffProps) => {
  const filteredPreviousVerificationTemplateVersion = omit(
    ['created-at', 'updated-at', 'status', 'name'],
    previousVerificationTemplateVersion.attributes,
  );
  const filteredCurrentVerificationTemplateVersion = omit(
    ['created-at', 'updated-at', 'status', 'name'],
    currentVerificationTemplateVersion.attributes,
  );
  const verificationTemplateDiff = create().diff(
    filteredPreviousVerificationTemplateVersion,
    filteredCurrentVerificationTemplateVersion,
  );

  return (
    <DiffViewCard
      title={getVtmplvDisplayName(currentVerificationTemplateVersion)}
      description='Checks and verification type-specific configurations'
      status={
        isEmpty(filteredCurrentVerificationTemplateVersion)
          ? 'success'
          : isEmpty(filteredPreviousVerificationTemplateVersion)
            ? 'danger'
            : undefined
      }
      diff={verificationTemplateDiff}
      baseObject={filteredCurrentVerificationTemplateVersion}
    />
  );
};

export default VerificationTemplateDiff;
